import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Type, Sparkles, ExternalLink } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cmsFields, groupIcons } from "./cms/cmsFieldsConfig";
import CmsFieldRenderer from "./cms/CmsFieldRenderer";
import LocaleTabs, { LocaleCode, storageKeyFor } from "@/components/admin/LocaleTabs";
import { translateFields, AUTO_LOCALES } from "@/lib/cms-translate";

const ContentManager = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [locale, setLocale] = useState<LocaleCode>("pt-PT");
  const [translating, setTranslating] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    supabase.from("app_settings").select("key, value").then(({ data }) => {
      const map: Record<string, string> = {};
      (data || []).forEach((row: any) => {
        let val = row.value;
        if (typeof val === "string") {
          try { val = JSON.parse(val); } catch {}
        }
        map[row.key] = typeof val === "string" ? val : JSON.stringify(val);
      });
      setValues(map);
    });
  }, []);

  const autoTranslateBase = async (baseKey: string, ptValue: string, silent = false) => {
    const field = cmsFields.find((f) => f.key === baseKey);
    if (!field || (field.type !== "text" && field.type !== "textarea")) return;
    if (!ptValue.trim()) return;
    if (!silent) toast({ title: `A traduzir "${field.label}"...` });

    const res = await translateFields([{ key: baseKey, value: ptValue }], {
      existing: values,
      onValue: (sKey, val) => setValues((p) => ({ ...p, [sKey]: val })),
    });

    if (!silent) {
      if (res.translatedFields === 0 && res.failedLocales.length === 0) {
        toast({ title: "Já traduzido, nada a atualizar " });
      } else {
        toast({
          title: res.failedLocales.length
            ? `Traduzido parcialmente (falhou: ${res.failedLocales.join(", ")})`
            : `Traduzido em ${AUTO_LOCALES.length} idiomas `,
          variant: res.failedLocales.length ? "destructive" : "default",
        });
      }
    }
    return res;
  };

  const syncAllTranslations = async (force = false) => {
    const fields = cmsFields
      .filter((f) => f.type === "text" || f.type === "textarea")
      .map((f) => ({ key: f.key, value: values[f.key] || "" }))
      .filter((f) => f.value.trim());

    setSyncing(true);
    toast({ title: force ? "A retraduzir todo o site..." : "A traduzir apenas o que mudou..." });
    const res = await translateFields(fields, {
      force,
      existing: values,
      onValue: (sKey, val) => setValues((p) => ({ ...p, [sKey]: val })),
    });
    setSyncing(false);

    if (res.translatedFields === 0 && !res.failedLocales.length) {
      toast({ title: "Tudo já estava traduzido  (0 tokens gastos)" });
    } else {
      toast({
        title: `${res.translatedFields} traduções atualizadas · ${res.skipped} já atualizadas`,
        description: res.failedLocales.length ? `Falhou em: ${res.failedLocales.join(", ")}` : undefined,
        variant: res.failedLocales.length ? "destructive" : "default",
      });
    }
  };



  const handleSave = async (key: string) => {
    setSaving((p) => ({ ...p, [key]: true }));
    const val = values[key] ?? "";
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key, value: val }, { onConflict: "key" });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else if (!key.includes("__")) {
      // Base (pt-PT) key just saved, fan-out translations to all other locales.
      autoTranslateBase(key, val);
    }
    setSaving((p) => ({ ...p, [key]: false }));
  };


  const handleImageUpload = async (key: string, file: File) => {
    setUploading((p) => ({ ...p, [key]: true }));
    const ext = file.name.split(".").pop();
    const path = `${key}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("cms-images")
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast({ title: "Erro no upload", description: uploadError.message, variant: "destructive" });
      setUploading((p) => ({ ...p, [key]: false }));
      return;
    }
    const { data: urlData } = supabase.storage.from("cms-images").getPublicUrl(path);
    const url = urlData.publicUrl;
    setValues((p) => ({ ...p, [key]: url }));
    await supabase.from("app_settings").upsert({ key, value: url }, { onConflict: "key" });
    toast({ title: "Imagem carregada!" });
    setUploading((p) => ({ ...p, [key]: false }));
  };

  const handleRemoveImage = async (key: string) => {
    setValues((p) => ({ ...p, [key]: "" }));
    await supabase.from("app_settings").upsert({ key, value: "" }, { onConflict: "key" });
    toast({ title: "Imagem removida!" });
  };

  const handleChange = (key: string, value: string) => {
    setValues((p) => ({ ...p, [key]: value }));
  };

  const translateGroup = async (group: string) => {
    if (locale === "pt-PT") {
      toast({ title: "Selecione outro idioma para traduzir." });
      return;
    }
    setTranslating(group);
    const fields = cmsFields.filter(
      (f) => f.group === group && (f.type === "text" || f.type === "textarea")
    );
    const texts = fields
      .map((f) => ({ key: f.key, value: values[f.key] || "" }))
      .filter((t) => t.value.trim());
    if (!texts.length) {
      toast({ title: "Nada para traduzir neste grupo." });
      setTranslating(null);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke("translate", {
        body: { texts, targetLang: locale },
      });
      if (error) throw error;
      const translated: { key: string; value: string }[] = data?.translated || [];
      const updates = translated.map((t) => ({
        key: storageKeyFor(t.key, locale),
        value: t.value,
      }));
      if (updates.length) {
        await supabase.from("app_settings").upsert(updates, { onConflict: "key" });
        setValues((p) => {
          const next = { ...p };
          updates.forEach((u, i) => { next[u.key] = translated[i].value; });
          return next;
        });
      }
      toast({ title: `Traduzido ${translated.length} campos (${locale}) ` });
    } catch (e: any) {
      toast({ title: "Erro na tradução", description: e.message, variant: "destructive" });
    } finally {
      setTranslating(null);
    }
  };

  const groups = [...new Set(cmsFields.map((f) => f.group))];

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Conteúdo do Site</h1>
          <p className="text-muted-foreground">
            Edite todos os textos, imagens e cores. As alterações guardam-se automaticamente.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={syncing}
            onClick={() => syncAllTranslations(false)}
            className="gap-1"
          >
            <Sparkles size={14} /> {syncing ? "A traduzir..." : "Traduzir site completo"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={syncing}
            onClick={() => syncAllTranslations(true)}
            className="gap-1 text-xs"
            title="Ignora a cache e retraduz tudo"
          >
            Forçar retradução
          </Button>
          <a
            href="/?edit=true"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink size={14} /> Ver site
          </a>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <span className="text-sm font-medium">Idioma:</span>
        <LocaleTabs value={locale} onChange={setLocale} />
        {locale !== "pt-PT" && (
          <p className="text-xs text-muted-foreground">
            Deixe em branco para usar automaticamente o PT-PT.
          </p>
        )}
      </div>

      <div className="max-w-2xl">
        <Accordion type="multiple" defaultValue={["Hero"]} className="space-y-3">
          {groups.map((group) => {
            const GroupIcon = groupIcons[group] || Type;
            const fields = cmsFields.filter((f) => f.group === group);
            return (
              <AccordionItem key={group} value={group} className="rounded-2xl border border-border bg-card px-6 overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-5">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                      <GroupIcon size={18} className="text-primary" />
                    </div>
                    <span className="font-display text-lg font-semibold">{group}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {fields.length} campos
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6 space-y-5">
                  {locale !== "pt-PT" && (
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => translateGroup(group)}
                        disabled={translating === group}
                        className="gap-1"
                      >
                        <Sparkles size={14} />
                        {translating === group ? "A traduzir..." : `Traduzir com IA (${locale})`}
                      </Button>
                    </div>
                  )}
                  {fields.map((field) => {
                    const sKey = storageKeyFor(field.key, locale);
                    return (
                      <CmsFieldRenderer
                        key={sKey}
                        field={field}
                        storageKey={sKey}
                        baseValue={locale !== "pt-PT" ? values[field.key] : undefined}
                        value={values[sKey] || ""}
                        companionValues={values}
                        saving={!!saving[sKey]}
                        uploading={!!uploading[sKey]}
                        onChange={handleChange}
                        onSave={handleSave}
                        onImageUpload={handleImageUpload}
                        onRemoveImage={handleRemoveImage}
                      />
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
};

export default ContentManager;
